import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SEOHelmet from '../../components/SEOHelmet';
import { motion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';

import PublicNavigation from '../../components/ui/PublicNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { generateProfessionalQuote, calculateBaseEstimate } from '../../services/professionalQuoteService';
import { prospectService } from '../../services/prospectService';
import { professionalClientAuthService } from '../../services/professionalClientAuthService';
import { professionalQuoteService } from '../../services/professionalQuoteService';
import UnifiedFooter from '../../components/ui/UnifiedFooter';

const ProfessionalServicesLandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    clientType: '',
    shippingType: '',
    needsEX1: '',
    pickupAddress: '',
    frequency: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedQuote, setGeneratedQuote] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [showQuote, setShowQuote] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showStickyBanner, setShowStickyBanner] = useState(true);

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e?.target?.value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validation
    if (!formData?.companyName || !formData?.contactName || !formData?.email || !formData?.shippingType) {
      toast?.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);

      // Generate quote with OpenAI
      const quoteResult = await professionalQuoteService?.generateProfessionalQuote(formData);

      if (!quoteResult?.success) {
        throw new Error('Erreur lors de la génération du devis');
      }

      // Extract estimated amount from quote (simple heuristic)
      const estimatedAmount = professionalQuoteService?.calculateBaseEstimate(
        formData?.shippingType,
        10
      )?.price;

      // Check if professional client is authenticated
      const currentClient = await professionalClientAuthService?.getCurrentProfessionalClient();

      // Create prospect with professional client link
      const prospectResult = await prospectService?.createProfessionalProspectWithQuote({
        companyName: formData?.companyName,
        contactName: formData?.contactName,
        email: formData?.email,
        phone: formData?.phone,
        clientType: formData?.clientType,
        shippingType: formData?.shippingType,
        needsEX1: formData?.needsEX1,
        pickupAddress: formData?.pickupAddress,
        frequency: formData?.frequency,
        message: formData?.message,
        quoteContent: quoteResult?.quote,
        estimatedAmount,
        professionalClientId: currentClient?.id || null
      });

      setGeneratedQuote(quoteResult?.quote);
      setShowQuote(true);

      toast?.success('Devis généré avec succès ! Email de confirmation envoyé.');

      // Reset form
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        clientType: '',
        shippingType: '',
        needsEX1: '',
        pickupAddress: '',
        frequency: '',
        message: ''
      });
    } catch (error) {
      console.error('Quote generation error:', error);
      toast?.error(error?.message || 'Erreur lors de la génération du devis');
    } finally {
      setLoading(false);
    }
  };

  const handleNewQuote = () => {
    setGeneratedQuote(null);
    setFormData({
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      clientType: '',
      shippingType: '',
      needsEX1: '',
      pickupAddress: '',
      frequency: '',
      message: ''
    });
    document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const targetClients = [
  {
    icon: 'ShoppingBag',
    title: 'Commerçants & importateurs',
    description: 'Vous importez des vêtements, de l\'électronique, des pièces détachées ou d\'autres marchandises vers la Côte d\'Ivoire ? Envoyez par volume, par barriques ou par conteneur complet selon vos besoins.'
  },
  {
    icon: 'HandHeart',
    title: 'Associations & ONG',
    description: 'Vous envoyez des dons, du matériel scolaire, médical ou humanitaire ? Nous accompagnons les associations avec des tarifs adaptés pour vos envois solidaires.'
  },
  {
    icon: 'Building2',
    title: 'Entreprises & envois réguliers',
    description: 'Vous expédiez régulièrement des marchandises vers Abidjan ? Bénéficiez d\'un compte pro avec des tarifs préférentiels, une ligne téléphonique dédiée et un interlocuteur attitré.'
  },
  {
    icon: 'PackageOpen',
    title: 'Déménagements complets',
    description: 'Vous rentrez en Côte d\'Ivoire ou vous installez un proche ? Nous prenons en charge votre déménagement complet avec un conteneur dédié.'
  }];


  const shippingSolutions = [
  {
    icon: 'Ruler',
    title: 'Envoi au volume',
    subtitle: 'Idéal quand vous n\'avez pas assez pour remplir un conteneur entier',
    features: [
    'Payez uniquement le volume que vous occupez (au m³)',
    'Pas besoin de barrique : envoyez vos cartons, palettes ou colis tels quels',
    'Vos marchandises voyagent dans notre conteneur groupé avec d\'autres envois',
    'Solution la plus flexible et économique pour les petits et moyens volumes',
    '2 départs par mois vers Abidjan'],

    buttonText: 'Devis envoi au volume',
    badge: null
  },
  {
    icon: 'Drum',
    title: 'Barriques en quantité',
    subtitle: 'Tarif dégressif dès 5 barriques',
    features: [
    'Barriques noires (270L) et bleues (220L) disponibles',
    'Tarif dégressif : plus vous envoyez, moins vous payez',
    'Collecte en Île-de-France ou enlèvement partout en France',
    '2 départs par mois vers Abidjan'],

    buttonText: 'Devis barriques pro',
    badge: 'POPULAIRE'
  },
  {
    icon: 'Container',
    title: 'Conteneur complet',
    subtitle: 'Pour les gros volumes et déménagements',
    features: [
    'Conteneur 20 pieds (environ 33 m³) pour les envois moyens',
    'Conteneur 40 pieds (environ 67 m³) pour les gros volumes',
    'Conteneur réservé exclusivement à votre marchandise',
    'Gestion complète : enlèvement, chargement, expédition, dédouanement',
    'Idéal pour les déménagements et les grosses commandes commerciales'],

    buttonText: 'Devis conteneur complet',
    badge: null
  }];


  const advantages = [
  {
    icon: 'FileCheck',
    title: 'Certificat EX1 & TVA',
    description: 'Nous gérons votre certificat EX1 pour que vous récupériez la TVA sur vos achats en France.'
  },
  {
    icon: 'Phone',
    title: 'Ligne pro dédiée',
    description: 'Un numéro de téléphone réservé aux professionnels. Pas d\'attente, un conseiller qui connaît votre dossier.'
  },
  {
    icon: 'Truck',
    title: 'Enlèvement partout en France',
    description: 'Nous venons chercher vos marchandises où que vous soyez en France métropolitaine.'
  },
  {
    icon: 'TrendingDown',
    title: 'Tarifs dégressifs',
    description: 'Plus vous envoyez, moins vous payez. Remises dès 5 barriques ou dès votre premier envoi au volume.'
  },
  {
    icon: 'Eye',
    title: 'Suivi personnalisé',
    description: 'Suivez votre envoi en temps réel, de l\'enlèvement en France à la réception à Abidjan.'
  },
  {
    icon: 'Shield',
    title: 'Dédouanement inclus',
    description: 'Nous gérons toutes les formalités douanières à l\'export (France) et à l\'import (Côte d\'Ivoire).'
  }];


  const processSteps = [
  {
    number: 1,
    title: 'Contactez votre conseiller pro',
    description: 'Appelez notre ligne pro dédiée ou remplissez le formulaire. Décrivez votre besoin : type de marchandise, volume, fréquence.'
  },
  {
    number: 2,
    title: 'Devis personnalisé sous 24h',
    description: 'Nous vous envoyons un devis adapté : envoi au volume, par barriques ou conteneur complet. Si vous avez besoin du certificat EX1, nous l\'intégrons au devis.'
  },
  {
    number: 3,
    title: 'Enlèvement de vos marchandises',
    description: 'Nous organisons l\'enlèvement chez vous ou à l\'adresse de votre choix, partout en France. Vos marchandises sont acheminées vers notre entrepôt.'
  },
  {
    number: 4,
    title: 'Expédition & suivi',
    description: 'Vos marchandises partent dans le prochain conteneur vers Abidjan. Vous suivez votre envoi en temps réel grâce à votre espace client.'
  },
  {
    number: 5,
    title: 'Dédouanement & réception à Abidjan',
    description: 'Nous gérons le dédouanement complet. Vos marchandises sont disponibles au point de retrait à Abidjan ou livrées à l\'adresse de votre choix.'
  }];


  const testimonials = [
  {
    name: 'Moussa B.',
    activity: 'Commerçant textile, Paris',
    text: 'J\'envoie entre 10 et 15 barriques par mois à Abidjan pour approvisionner ma boutique. Le tarif dégressif et le certificat EX1 me permettent de vraiment optimiser mes coûts. Et avec la ligne pro, j\'ai toujours quelqu\'un au bout du fil.'
  },
  {
    name: 'Association Solidarité Côte d\'Ivoire',
    activity: 'ONG, Île-de-France',
    text: 'Nous envoyons régulièrement du matériel scolaire depuis Lyon. ADT Express organise l\'enlèvement directement dans nos locaux et gère tout jusqu\'à Abidjan. C\'est un vrai soulagement pour notre petite équipe.'
  },
  {
    name: 'Koné Trading',
    activity: 'Import-export, Marseille',
    text: 'On envoie au volume chaque mois. Pas besoin de remplir un conteneur entier, on paie juste ce qu\'on utilise. Le suivi est top et le dédouanement est géré par leur équipe.'
  }];


  const faqs = [
  {
    question: 'À partir de combien de barriques puis-je bénéficier d\'un tarif pro ?',
    answer: 'Les tarifs dégressifs s\'appliquent dès 5 barriques. Plus le volume est important, plus le tarif unitaire diminue. Contactez-nous pour recevoir une grille tarifaire personnalisée.'
  },
  {
    question: 'Je n\'ai pas assez de marchandises pour un conteneur entier, comment faire ?',
    answer: 'Pas de problème ! Vous pouvez envoyer au volume (au m³). Vos marchandises voyagent dans notre conteneur groupé. Vous payez uniquement l\'espace que vous occupez. C\'est la solution idéale pour les petits et moyens volumes.'
  },
  {
    question: 'Qu\'est-ce que le certificat EX1 et comment récupérer la TVA ?',
    answer: 'Le certificat EX1 est un document douanier français qui prouve que vos marchandises ont bien été exportées hors de l\'Union Européenne. Avec ce document visé par la douane, vous pouvez demander le remboursement de la TVA (20%) à vos fournisseurs français. ADT Express se charge de toute la procédure EX1 pour vous.'
  },
  {
    question: 'Quelle est la différence entre un conteneur 20 pieds et 40 pieds ?',
    answer: 'Le conteneur 20 pieds offre environ 33 m³, idéal pour les envois moyens ou les déménagements partiels. Le conteneur 40 pieds offre environ 67 m³, parfait pour les gros volumes commerciaux ou les déménagements complets.'
  },
  {
    question: 'Pouvez-vous venir chercher mes marchandises en dehors de Paris ?',
    answer: 'Oui ! Nous organisons l\'enlèvement de vos marchandises partout en France métropolitaine : Paris, Lyon, Marseille, Bordeaux, Lille, et toutes les autres villes. Nous coordonnons le transport jusqu\'à notre entrepôt puis l\'expédition vers Abidjan.'
  },
  {
    question: 'Comment fonctionne le suivi pro ?',
    answer: 'Chaque client pro dispose d\'un accès à un espace de suivi en temps réel. Vous pouvez suivre vos envois de l\'enlèvement en France jusqu\'à la réception à Abidjan. Vous avez aussi un conseiller dédié joignable sur votre ligne pro.'
  },
  {
    question: 'Quels types de marchandises puis-je envoyer ?',
    answer: 'Vous pouvez envoyer la plupart des marchandises : vêtements, électronique, électroménager, pièces détachées, mobilier, matériel scolaire, denrées non périssables, etc. Les produits dangereux, périssables et interdits à l\'exportation ne sont pas acceptés. Contactez-nous pour vérifier si votre marchandise est éligible.'
  }];

  // Add this block - ex1Benefits array declaration
  const ex1Benefits = [
  {
    icon: 'FileCheck',
    title: 'Récupération TVA simplifiée',
    description: 'Nous gérons votre certificat EX1 et vous aidons à récupérer jusqu\'à 20% de TVA sur vos achats professionnels en France.'
  },
  {
    icon: 'Shield',
    title: 'Conformité douanière garantie',
    description: 'Tous vos documents sont centralisés dans votre espace pro : certificats EX1, factures, bordereaux de douane.'
  },
  {
    icon: 'TrendingDown',
    title: 'Économies substantielles',
    description: 'La récupération de TVA peut représenter jusqu\'à 20% d\'économies sur vos coûts d\'approvisionnement.'
  }];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowStickyBanner(false);
      } else {
        setShowStickyBanner(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const structuredDataPro = {
    "@context": "https://schema.org",
    "@graph": [
    {
      "@type": "Service",
      "name": "Services Professionnels ADT Express",
      "description": "Plateforme logistique digitale pour professionnels - Corridor France Côte d'Ivoire",
      "provider": {
        "@type": "Organization",
        "name": "ADT Express"
      },
      "serviceType": "Logistique professionnelle France Côte d\'Ivoire",
      "areaServed": ["FR", "CI"]
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://www.adtexpress.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Professionnels",
        "item": "https://www.adtexpress.fr/pro"
      }]

    }]

  };

  return (
    <>
      <SEOHelmet
        title="Logistique Professionnelle France Côte d'Ivoire | Plateforme Digitale Fret Maritime - ADT Express"
        description="Plateforme logistique digitale pour professionnels : envoi conteneur France Côte d'Ivoire, fret maritime Paris Abidjan, groupage professionnel. Suivi temps réel, gestion documentaire, certificats EX1."
        keywords={"logistique professionnelle France Côte d'Ivoire, plateforme fret maritime Paris Abidjan, envoi conteneur professionnel Abidjan, groupage maritime entreprise, transport barrique professionnel CI, envoyer barrique Côte d'Ivoire, groupage maritime, tarif envoi Abidjan"}
        canonicalUrl="https://www.adtexpress.fr/pro"
        structuredData={structuredDataPro} />
      <Helmet>
        <title>Envoi professionnel Côte d'Ivoire — Transport maritime pro, EX1 récupération TVA | ADT Express</title>
        <meta name="description" content="ADT Express pour les pros : envoi par volume, barriques, conteneur complet vers Abidjan. Certificat EX1 pour récupérer la TVA. Enlèvement partout en France. Devis gratuit." />
        <meta name="keywords" content="envoi professionnel Côte d'Ivoire, certificat EX1, récupération TVA export, conteneur Abidjan, transport maritime professionnel, envoi volume Côte d'Ivoire" />
      </Helmet>
      <Toaster position="top-right" />
      <div className="min-h-screen bg-white pt-44">
        <PublicNavigation />

        {/* Sticky Pro Line Banner */}
        {showStickyBanner &&
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          className="sticky top-16 z-40 bg-green-50 border-b border-green-200 py-3 px-4">
            <div className="max-w-7xl mx-auto flex items-center justify-center space-x-3 text-sm sm:text-base">
              <Icon name="Phone" size={20} className="text-green-700" />
              <span className="text-gray-800">
                <span className="font-semibold">📞 Ligne pro dédiée :</span> 06 51 98 86 21 — Lundi à Samedi, 9h-19h
              </span>
            </div>
          </motion.div>
        }

        {/* Hero Section - REPOSITIONNEMENT PREMIUM DIGITAL */}
        <section className="relative h-[700px] flex items-center justify-center overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1ab98dbc9-1771190424374.png"
              alt="Infrastructure logistique maritime France Côte d'Ivoire"
              className="w-full h-full object-cover" />
            
            <div className="absolute inset-0 bg-gradient-to-br from-green-900/90 via-green-800/85 to-green-900/90"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}>
              
              <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-6">
                <Icon name="Briefcase" size={20} />
                <span className="font-semibold">Opérateur Logistique Structuré</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Votre partenaire logistique France – Côte d'Ivoire pour piloter vos flux en toute maîtrise
              </h1>
              
              <p className="text-xl sm:text-2xl text-white/95 max-w-4xl mx-auto mb-8 leading-relaxed">
                Groupage maritime, conteneurs dédiés, certificat EX1, gestion documentaire et suivi centralisé depuis un espace professionnel sécurisé.
              </p>
              
              {/* 3 Bullet Points */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10 max-w-4xl mx-auto">
                <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <Icon name="Activity" size={24} className="text-green-300" />
                  <span className="text-white font-semibold text-lg">✔ Pilotage en temps réel</span>
                </div>
                <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <Icon name="FileCheck" size={24} className="text-green-300" />
                  <span className="text-white font-semibold text-lg">✔ Conformité douanière simplifiée</span>
                </div>
                <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <Icon name="LayoutDashboard" size={24} className="text-green-300" />
                  <span className="text-white font-semibold text-lg">✔ Centralisation complète de vos opérations</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  variant="default"
                  size="lg"
                  className="bg-white text-green-700 hover:bg-gray-50 shadow-2xl text-lg px-8 py-6 font-bold"
                  onClick={() => window.location.href = '/professional-client-login'}>
                  <Icon name="LayoutDashboard" size={24} className="mr-2" />
                  Accéder à mon espace pro
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-white text-white hover:bg-white hover:text-green-700 text-lg px-8 py-6 font-bold"
                  onClick={() => document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' })}>
                  
                  <Icon name="FileText" size={24} className="mr-2" />
                  Demander un devis professionnel
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Professional Client Login Banner */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          
          <div className="bg-gradient-to-br from-green-700 to-green-800 rounded-2xl shadow-2xl p-8 sm:p-10 border-2 border-green-600">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center space-x-4 text-white">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                  <Icon name="UserCheck" size={32} color="white" />
                </div>
                <div className="text-left">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-1">
                    Déjà client professionnel ?
                  </h3>
                  <p className="text-green-100 text-base sm:text-lg">
                    Accédez à votre tableau de bord et gérez vos envois
                  </p>
                </div>
              </div>
              <Link
                to="/professional-client-login"
                className="inline-flex items-center justify-center space-x-3 bg-white hover:bg-gray-50 active:bg-gray-100 text-green-700 font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 min-h-[56px] text-lg whitespace-nowrap">
                
                <Icon name="LogIn" size={24} />
                <span>Je me connecte</span>
              </Link>
            </div>
          </div>
        </motion.section>

        {/* Target Clients Section */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="bg-gray-50 py-16">
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                À qui s'adresse notre offre pro ?
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Notre offre s'adresse aux entreprises et organisations ayant besoin d'une solution logistique fiable, structurée et pilotable à distance entre la France et la Côte d'Ivoire.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {targetClients?.map((client, index) =>
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all border border-gray-100">
                
                  <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon name={client?.icon} size={28} className="text-green-700" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{client?.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{client?.description}</p>
                </motion.div>
              )}
            </div>
          </div>
        </motion.section>

        {/* EX1 Certificate Section - Enhanced */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="bg-gradient-to-b from-white to-gray-50 py-16">
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Certificat EX1 : simplifiez votre TVA et sécurisez vos flux
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Nous prenons en charge la gestion et l'émission des certificats EX1 pour optimiser vos opérations fiscales et douanières entre la France et la Côte d'Ivoire.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Benefit 1 */}
              <motion.div
                variants={itemVariants}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="TrendingDown" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">✔ Récupération TVA facilitée</h3>
                <p className="text-gray-600 leading-relaxed">Nous gérons votre certificat EX1 et vous aidons à récupérer jusqu'à 20% de TVA sur vos achats professionnels en France.</p>
              </motion.div>

              {/* Benefit 2 */}
              <motion.div
                variants={itemVariants}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="FolderCheck" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">✔ Dossier douanier centralisé</h3>
                <p className="text-gray-600 leading-relaxed">Tous vos documents sont centralisés dans votre espace pro : certificats EX1, factures, bordereaux de douane.</p>
              </motion.div>

              {/* Benefit 3 */}
              <motion.div
                variants={itemVariants}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="Users" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">✔ Moins d'intermédiaires, plus de contrôle</h3>
                <p className="text-gray-600 leading-relaxed">Gestion directe de vos opérations sans multiplier les interlocuteurs. Un seul partenaire pour toute la chaîne logistique.</p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Process Timeline Section */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="bg-white py-16">
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Comment ça marche pour les pros ?
              </h2>
            </div>
            <div className="max-w-4xl mx-auto space-y-8">
              {processSteps?.map((step, index) =>
              <motion.div
                key={index}
                variants={itemVariants}
                className="flex items-start space-x-6 bg-white rounded-xl p-6 shadow-md">
                
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {step?.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step?.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step?.description}</p>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.section>

        {/* Cockpit Logistique Section - Strategic Positioning with Premium Encadré */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-24">
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Premium Encadré - Strategic Message */}
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-10 shadow-2xl border-2 border-green-500 mb-16">
              <div className="text-center mb-6">
                <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full mb-4">
                  <Icon name="Award" size={20} />
                  <span className="font-bold text-sm">Plateforme digitale exclusive</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                  Un cockpit logistique digital dédié aux professionnels
                </h2>
              </div>
              <p className="text-xl text-white/95 max-w-4xl mx-auto leading-relaxed text-center">
                Accédez à un tableau de bord sécurisé vous permettant de centraliser vos expéditions, certificats EX1, factures, documents douaniers, analyses de coûts et historiques d'opérations. Une interface pensée pour les importateurs et entreprises exigeantes.
              </p>
            </motion.div>

            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.h3
                variants={itemVariants}
                className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Votre Espace Pro : pilotez votre logistique depuis un tableau de bord unique
              </motion.h3>
              
              <motion.p
                variants={itemVariants}
                className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                En tant que client professionnel, vous disposez d'un dashboard personnel sécurisé vous permettant de centraliser :
              </motion.p>
            </div>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {/* Feature 1 */}
              <motion.div
                variants={itemVariants}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 hover:border-green-500/50 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="Package" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Vos expéditions en cours</h3>
                <p className="text-gray-400 leading-relaxed">Suivez en temps réel l'état de toutes vos expéditions depuis l'enlèvement jusqu'à la livraison</p>
              </motion.div>

              {/* Feature 2 */}
              <motion.div
                variants={itemVariants}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 hover:border-green-500/50 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="FileText" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Vos factures et documents</h3>
                <p className="text-gray-400 leading-relaxed">Accédez instantanément à toutes vos factures, bordereaux et documents administratifs</p>
              </motion.div>

              {/* Feature 3 */}
              <motion.div
                variants={itemVariants}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 hover:border-green-500/50 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="FileCheck" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Vos certificats EX1</h3>
                <p className="text-gray-400 leading-relaxed">Téléchargez vos certificats EX1 visés pour récupérer la TVA sur vos achats</p>
              </motion.div>

              {/* Feature 4 */}
              <motion.div
                variants={itemVariants}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 hover:border-green-500/50 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="Camera" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Vos photos d'expédition</h3>
                <p className="text-gray-400 leading-relaxed">Visualisez les photos de vos marchandises à chaque étape du transport</p>
              </motion.div>

              {/* Feature 5 */}
              <motion.div
                variants={itemVariants}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 hover:border-green-500/50 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="TrendingUp" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Vos analyses de coûts</h3>
                <p className="text-gray-400 leading-relaxed">Consultez vos statistiques d'envoi et optimisez vos coûts logistiques</p>
              </motion.div>

              {/* Feature 6 */}
              <motion.div
                variants={itemVariants}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 hover:border-green-500/50 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="History" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Vos historiques d'opérations</h3>
                <p className="text-gray-400 leading-relaxed">Retrouvez l'intégralité de vos expéditions passées avec tous les détails</p>
              </motion.div>

              {/* Feature 7 */}
              <motion.div
                variants={itemVariants}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 hover:border-green-500/50 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-300 md:col-span-2 lg:col-span-1">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="Bell" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Vos notifications temps réel</h3>
                <p className="text-gray-400 leading-relaxed">Recevez des alertes instantanées à chaque étape importante de vos envois</p>
              </motion.div>
            </div>

            {/* Premium Card with Badges */}
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-10 shadow-2xl border-2 border-green-500">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                <div className="flex-1 text-center lg:text-left">
                  <h3 className="text-3xl font-bold text-white mb-4">
                    Une plateforme complète pour piloter votre logistique
                  </h3>
                  <p className="text-green-50 text-lg mb-6">
                    Gérez l'intégralité de vos opérations France – Côte d'Ivoire depuis un espace unique et sécurisé
                  </p>
                  
                  {/* Badges Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 flex items-center justify-center space-x-2">
                      <Icon name="Clock" size={20} color="white" />
                      <span className="text-white font-semibold text-sm">Accès 24/24</span>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 flex items-center justify-center space-x-2">
                      <Icon name="Activity" size={20} color="white" />
                      <span className="text-white font-semibold text-sm">Suivi temps réel</span>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 flex items-center justify-center space-x-2">
                      <Icon name="Archive" size={20} color="white" />
                      <span className="text-white font-semibold text-sm">Historique complet</span>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 flex items-center justify-center space-x-2">
                      <Icon name="Download" size={20} color="white" />
                      <span className="text-white font-semibold text-sm">Documents téléchargeables</span>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 flex items-center justify-center space-x-2">
                      <Icon name="Headphones" size={20} color="white" />
                      <span className="text-white font-semibold text-sm">Conseiller dédié</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex-shrink-0">
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-white text-green-700 hover:bg-gray-50 shadow-2xl text-lg px-10 py-6 font-bold"
                    onClick={() => window.location.href = '/professional-client-login'}>
                    <Icon name="ArrowRight" size={24} className="mr-2" />
                    Découvrir l'Espace Pro
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* EX1 Certificate Section */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="bg-gradient-to-b from-white to-gray-50 py-16">
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Certificat EX1 : Récupérez votre TVA en toute simplicité
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Grâce à notre espace pro, vous suivez vos certificats EX1, factures et documents douaniers en un seul endroit.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {ex1Benefits?.map((benefit, index) =>
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                
                  <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center mb-4">
                    <Icon name={benefit?.icon} size={28} color="white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit?.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit?.description}</p>
                </motion.div>
              )}
            </div>
          </div>
        </motion.section>

        {/* Comparative Section - Why Choose ADT Express */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="bg-white py-16">
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Pourquoi les professionnels choisissent ADT Express
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Une infrastructure logistique digitale complète pour piloter vos flux France – Côte d'Ivoire
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Card 1 */}
              <motion.div
                variants={itemVariants}
                className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all border-2 border-green-200">
                <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="LayoutDashboard" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Espace client digital sécurisé</h3>
                <p className="text-gray-700 leading-relaxed">Tableau de bord complet pour piloter vos expéditions, documents et analyses en temps réel</p>
              </motion.div>

              {/* Card 2 */}
              <motion.div
                variants={itemVariants}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all border-2 border-blue-200">
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="FileText" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Gestion documentaire intégrée</h3>
                <p className="text-gray-700 leading-relaxed">Certificats EX1, factures, bordereaux douaniers centralisés et téléchargeables à tout moment</p>
              </motion.div>

              {/* Card 3 */}
              <motion.div
                variants={itemVariants}
                className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all border-2 border-purple-200">
                <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="Container" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Groupage structuré & planifié</h3>
                <p className="text-gray-700 leading-relaxed">2 départs garantis par mois avec optimisation des coûts et délais maîtrisés</p>
              </motion.div>

              {/* Card 4 */}
              <motion.div
                variants={itemVariants}
                className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all border-2 border-orange-200">
                <div className="w-14 h-14 bg-orange-600 rounded-xl flex items-center justify-center mb-4">
                  <Icon name="Eye" size={28} color="white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Suivi logistique transparent</h3>
                <p className="text-gray-700 leading-relaxed">Visibilité complète de l'enlèvement en France jusqu'à la livraison à Abidjan</p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="bg-gradient-to-br from-green-600 to-green-700 py-16">
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              variants={itemVariants}
              className="max-w-4xl mx-auto">
              
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Prêt à piloter vos flux internationaux ?
              </h2>
              
              <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
                Créez votre espace pro et gérez tous vos envois depuis la France jusqu'à la Côte d'Ivoire.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  className="bg-white text-green-700 hover:bg-gray-50 shadow-2xl text-lg px-8 py-6 font-bold"
                  onClick={() => window.location.href = '/professional-client-login'}>
                  
                  <Icon name="ArrowRight" size={24} className="mr-2" />
                  Créer mon espace pro
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-white text-white hover:bg-white hover:text-green-700 text-lg px-8 py-6 font-bold"
                  onClick={() => document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' })}>
                  
                  <Icon name="FileText" size={24} className="mr-2" />
                  Demander un devis professionnel
                </Button>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Quote Form Section */}
        <motion.section
          id="quote-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gray-50 py-16">
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  Demander une étude logistique personnalisée
                </h2>
                <p className="text-gray-600 text-lg">
                  Réponse sous 24h avec proposition adaptée à votre volume et fréquence d'expédition.
                </p>
              </div>

              {!generatedQuote ?
              <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                    label="Nom de l'entreprise / association *"
                    value={formData?.companyName}
                    onChange={handleInputChange('companyName')}
                    required
                    placeholder="Votre entreprise" />
                  
                    <Input
                    label="Nom du contact *"
                    value={formData?.contactName}
                    onChange={handleInputChange('contactName')}
                    required
                    placeholder="Votre nom" />
                  
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                    label="Email *"
                    type="email"
                    value={formData?.email}
                    onChange={handleInputChange('email')}
                    required
                    placeholder="votre@email.com" />
                  
                    <Input
                    label="Téléphone *"
                    type="tel"
                    value={formData?.phone}
                    onChange={handleInputChange('phone')}
                    required
                    placeholder="06 XX XX XX XX" />
                  
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Type de client *
                      </label>
                      <select
                      value={formData?.clientType}
                      onChange={handleInputChange('clientType')}
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors min-h-[44px] text-gray-900 bg-white">
                        <option value="">Sélectionnez</option>
                        <option value="merchant">Commerçant</option>
                        <option value="association">Association-ONG</option>
                        <option value="company">Entreprise</option>
                        <option value="moving">Déménagement</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Type d'envoi souhaité *
                      </label>
                      <select
                      value={formData?.shippingType}
                      onChange={handleInputChange('shippingType')}
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors min-h-[44px] text-gray-900 bg-white">
                        <option value="">Sélectionnez</option>
                        <option value="volume">Envoi au volume (m³)</option>
                        <option value="barrels">Barriques en quantité</option>
                        <option value="container20">Conteneur 20 pieds</option>
                        <option value="container40">Conteneur 40 pieds</option>
                        <option value="unsure">Je ne sais pas encore</option>
                      </select>
                    </div>
                  </div>

                  <Input
                  label="Adresse d'enlèvement (ville ou code postal) *"
                  value={formData?.pickupAddress}
                  onChange={handleInputChange('pickupAddress')}
                  required
                  placeholder="Paris, Lyon, Marseille..." />
                

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Message / détails supplémentaires
                    </label>
                    <textarea
                    value={formData?.message}
                    onChange={handleInputChange('message')}
                    rows={4}
                    placeholder="Décrivez votre besoin, type de marchandise, volume estimé..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-gray-900 placeholder-gray-500 bg-white" />
                  
                  </div>

                  <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6"
                  disabled={isSubmitting}>
                  {isSubmitting ?
                  <span className="flex items-center justify-center space-x-2">
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span>Génération du devis...</span>
                    </span> :

                  <span className="flex items-center justify-center space-x-2">
                      <Icon name="FileText" size={20} />
                      <span>Générer mon devis instantané</span>
                    </span>
                  }
                  </Button>

                  <p className="text-center text-gray-600 text-sm">
                    Devis généré sous 24h. Gratuit et sans engagement.
                  </p>
                </form> :

              <div id="generated-quote" className="space-y-6">
                  {/* Quote Display */}
                  <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-green-500">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                          <Icon name="CheckCircle2" size={24} color="white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">Devis Personnalisé</h3>
                          <p className="text-sm text-gray-600">Généré pour {generatedQuote?.clientInfo?.company}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Date</p>
                        <p className="text-sm font-medium text-gray-700">
                          {new Date(generatedQuote?.timestamp)?.toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                        {generatedQuote?.quote}
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="Phone" size={20} color="white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">Prochaines étapes</h4>
                        <p className="text-gray-700 mb-3">
                          Notre équipe va vous contacter sous 24h à l'adresse <span className="font-semibold">{generatedQuote?.clientInfo?.email}</span> pour finaliser votre devis et répondre à vos questions.
                        </p>
                        <p className="text-gray-700">
                          Pour toute question immédiate, appelez notre ligne pro : <span className="font-bold text-green-700">06 51 98 86 21</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                    variant="default"
                    size="lg"
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => window.print()}>
                    
                      <Icon name="Printer" size={20} className="mr-2" />
                      Imprimer le devis
                    </Button>
                    <Button
                    variant="outline"
                    size="lg"
                    className="flex-1 border-green-600 text-green-700 hover:bg-green-600 hover:text-white"
                    onClick={handleNewQuote}>
                    
                      <Icon name="Plus" size={20} className="mr-2" />
                      Nouveau devis
                    </Button>
                  </div>
                </div>
              }
            </div>
          </div>
        </motion.section>

        {/* Contextual Link to Pricing */}
        <section className="py-8 px-4">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-6 border-l-4 border-green-600 shadow-sm">
              <p className="text-base md:text-lg text-gray-700">
                💡 <strong>Besoin de transparence tarifaire ?</strong> Consultez également nos{' '}
                <Link
                  to="/dedicated-pricing-page"
                  className="text-green-700 font-semibold hover:text-green-800 underline decoration-2 underline-offset-4 hover:decoration-green-600 transition-all">
                  tarifs détaillés pour les expéditions en groupage maritime
                </Link>
                {' '}afin de mieux anticiper vos coûts logistiques France – Côte d'Ivoire.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Schema Service – Page Professionnels */}
<script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Logistique internationale France – Côte d'Ivoire pour professionnels",
              "provider": {
                "@type": "Organization",
                "name": "ADT Express",
                "url": "https://adtexpress.cloud"
              },
              "areaServed": {
                "@type": "Country",
                "name": "Côte d'Ivoire"
              },
              "serviceType": "Groupage maritime professionnel, conteneurs export, gestion EX1",
              "description": "Solutions logistiques pour entreprises exportant vers Abidjan : groupage maritime, conteneurs dédiés, gestion certificat EX1, tableau de bord digital sécurisé."
            })
          }} />
        

<UnifiedFooter />

      </div>
    </>);








};

export default ProfessionalServicesLandingPage;